import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadJobSectorDetails } from '../../../store/directusService';
import {
    createParticipantJobSearch,
    updateParticipantJobSearch
} from '../../../store/participantService';
import { getConfiguredItems } from '../../../utils/directusFunctions';
import { JOB_SECTOR_DETAILS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { trimEntries } from '../../../utils/objectUtils';
import DateSelect from '../../formElements/DateSelect';
import RadioButtons from '../../formElements/RadioButtons';
import SingleSelect from '../../formElements/SingleSelect';
import TextInputField from '../../formElements/TextInputField';
import FormActions from '../../ui/formActions/FormActions';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { TEXT_FIELD_LENGTH, validate } from './validateCreateEditJobSearch';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';

const CreateEditJobSearch = ({ row, onCancel, isDisabled, formType }) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        dateApplied: '',
        jobSectorId: '',
        businessName: '',
        jobTitle: '',
        interviewDate: '',
        interviewAttended: 'NOT KNOWN',
        jobOffered: 'NOT KNOWN',
        jobAccepted: 'NOT KNOWN'
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialEntryState, setInitialEntryState] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [jobSectorArray, setJobSectorArray] = useState([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const jobSectorDetails = useSelector(
        (state) => state.entities.directusService.jobSectorDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        !jobSectorDetails?.length && dispatch(loadJobSectorDetails());

        return () => {
            setErrors({});
            setNewEntry(initialState);
            setInitialEntryState(initialState);
        };
    }, []);

    useEffect(() => {
        if (formType === 'edit') {
            setNewEntry(row);
            setInitialEntryState(row);
        } else {
            setNewEntry((prev) => ({ ...prev, participantId: currentParticipant.id }));
        }
    }, [row]);

    useEffect(() => {
        setJobSectorArray(getConfiguredItems(jobSectorDetails, currentParticipant.contractId));
    }, [jobSectorDetails, currentParticipant.contractId]);

    useEffect(() => {
        setIsButtonDisabled(false);
        if (successMessage === 'Job Search created' || successMessage === 'Job Search Updated') {
            onCancel();
        }
    }, [successMessage]);

    useEffect(() => {
        setIsButtonDisabled(!!Object.keys(errors).length);
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        setErrors(trimEntries([key], errors));
    };

    const loadingError = () => {
        if (!jobSectorDetails?.length || !jobSectorArray?.length) return JOB_SECTOR_DETAILS_ERROR;
    };

    // EVENT HANDLERS
    const onUpdate = (key, val) => {
        setNewEntry((prev) => ({ ...prev, [key]: val }));
        clearError(key);
    };

    const onSubmit = () => {
        const newErrors = validate(newEntry, initialEntryState, formType);
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        setIsButtonDisabled(true);
        formType === 'edit'
            ? dispatch(updateParticipantJobSearch(newEntry))
            : dispatch(createParticipantJobSearch(newEntry));
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div className={form.formWrapper}>
            <h2 className={app.sectionHeading}>
                {newEntry.id ? 'Update Job Search' : 'Create Job Search'}
            </h2>
            <div className={form.formSection}>
                <div className={form.formColumn}>
                    <DateSelect
                        id="dateApplied"
                        label="Date Applied"
                        mandatory={true}
                        disabled={isDisabled}
                        max={new Date().toISOString().split('T')[0]}
                        value={newEntry.dateApplied}
                        error={errors?.dateApplied}
                        onDateChange={(date) => onUpdate('dateApplied', date)}
                    />
                    <SingleSelect
                        id="jobSectorId"
                        label="Job Sector"
                        placeholder="Select job Sector"
                        disabled={isDisabled}
                        menuItems={jobSectorArray || []}
                        selectedId={newEntry.jobSectorId || ''}
                        selected={
                            jobSectorArray.find((el) => el.id === newEntry.jobSectorId) || null
                        }
                        error={errors?.jobSectorId}
                        onChange={(id) => onUpdate('jobSectorId', id)}
                    />
                    <TextInputField
                        id={'businessName'}
                        label={'Business Name'}
                        placeholder={'Enter business name'}
                        disabled={isDisabled}
                        maxLength={TEXT_FIELD_LENGTH}
                        value={newEntry.businessName || ''}
                        error={errors?.businessName}
                        onChange={(e) => onUpdate('businessName', e.target.value)}
                    />
                    <TextInputField
                        id={'jobTitle'}
                        label={'Job Title'}
                        placeholder={'Enter job title'}
                        disabled={isDisabled}
                        maxLength={TEXT_FIELD_LENGTH}
                        value={newEntry.jobTitle || ''}
                        error={errors?.jobTitle}
                        onChange={(e) => onUpdate('jobTitle', e.target.value)}
                    />
                </div>
                <div className={form.formColumn}>
                    <DateSelect
                        id="interviewDate"
                        label="Interview Date"
                        disabled={isDisabled}
                        value={newEntry.interviewDate}
                        error={errors?.interviewDate}
                        onDateChange={(date) => onUpdate('interviewDate', date)}
                    />
                    <RadioButtons
                        id="interviewAttended"
                        label="Interview Attended"
                        disabled={isDisabled}
                        radioButtons={[
                            { id: 'yes', label: 'Yes', onChangeVal: 'YES' },
                            { id: 'no', label: 'No', onChangeVal: 'NO' },
                            { id: 'notKnown', label: 'Not Known', onChangeVal: 'NOT KNOWN' }
                        ]}
                        value={newEntry.interviewAttended}
                        onChange={(option) => onUpdate('interviewAttended', option)}
                    />
                    <RadioButtons
                        id="jobOffered"
                        label="Job Offered"
                        disabled={isDisabled}
                        radioButtons={[
                            { id: 'yes', label: 'Yes', onChangeVal: 'YES' },
                            { id: 'no', label: 'No', onChangeVal: 'NO' },
                            { id: 'notKnown', label: 'Not Known', onChangeVal: 'NOT KNOWN' }
                        ]}
                        value={newEntry.jobOffered}
                        onChange={(option) => onUpdate('jobOffered', option)}
                    />
                    <RadioButtons
                        id="jobAccepted"
                        label="Job Accepted"
                        disabled={isDisabled}
                        radioButtons={[
                            { id: 'yes', label: 'Yes', onChangeVal: 'YES' },
                            { id: 'no', label: 'No', onChangeVal: 'NO' },
                            { id: 'notKnown', label: 'Not Known', onChangeVal: 'NOT KNOWN' }
                        ]}
                        value={newEntry.jobAccepted}
                        onChange={(option) => onUpdate('jobAccepted', option)}
                    />
                </div>
            </div>
            <FormActions
                onClose={onSubmit}
                onCancel={onCancel}
                btnText={`${formType === 'edit' ? 'Update' : 'Add'} Job Search`}
                disabled={isDisabled || isButtonDisabled}
                error={errors?.button}
                clearError={() => clearError('button')}
            />
        </div>
    );
};

CreateEditJobSearch.propTypes = {
    row: PropTypes.object,
    onCancel: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool.isRequired,
    formType: PropTypes.string.isRequired
};

export default CreateEditJobSearch;
